import { useState, useEffect, useCallback } from 'react'
import { FileText, ChevronDown, RefreshCw, Truck, Check, AlertTriangle, ArrowRight } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import portariaService from '../../services/portariaService'
import leiloesService from '../../services/leiloesService'

export default function RastreamentoVendedor() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [expandido, setExpandido] = useState({})

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .catch(console.error)
      .finally(() => setLoadingL(false))
  }, [orgId])

  const carregar = useCallback(async () => {
    if (!orgId || !leilaoId) return
    setLoading(true)
    try {
      const data = await portariaService.rastreamentoVendedor(orgId, leilaoId)
      setDados(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [orgId, leilaoId])

  useEffect(() => { carregar() }, [carregar])

  const toggleVendedor = (nome) => {
    setExpandido(prev => ({ ...prev, [nome]: !prev[nome] }))
  }

  return (
    <div className="space-y-4">

      {/* Título */}
      <div className="flex items-center gap-2">
        <FileText className="w-5 h-5 text-teal-600" />
        <h1 className="text-lg font-semibold text-gray-800">Rastreamento por Vendedor</h1>
      </div>

      {/* Seletor de leilão */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <label className="text-xs text-gray-500 font-medium">Leilão</label>
            <div className="relative">
              <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)} disabled={loadingL}
                className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                {loadingL
                  ? <option>Carregando...</option>
                  : leiloes.map(l => (
                      <option key={l.id} value={String(l.id)}>
                        {l.nome} — {new Date(l.data_leilao).toLocaleDateString('pt-BR')}
                      </option>
                    ))
                }
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <button onClick={carregar} disabled={loading}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors self-end">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Totais gerais */}
      {dados && !loading && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-xs text-green-600 font-medium">Entrada Total</div>
              <div className="text-xl font-bold text-green-800">{dados.totais?.entrada || 0}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-xs text-blue-600 font-medium">Vendidos</div>
              <div className="text-xl font-bold text-blue-800">{dados.totais?.vendidos || 0}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3">
              <div className="text-xs text-red-600 font-medium">Defesa</div>
              <div className="text-xl font-bold text-red-800">{dados.totais?.defesa || 0}</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <div className="text-xs text-amber-600 font-medium">Não Mov.</div>
              <div className="text-xl font-bold text-amber-800">{dados.totais?.nao_movimentados || 0}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-xs text-purple-600 font-medium">Saldo Curral</div>
              <div className="text-xl font-bold text-purple-800">{dados.totais?.saldo_curral || 0}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
        </div>
      )}

      {/* Lista por vendedor */}
      {dados && !loading && dados.por_vendedor?.length > 0 && (
        <div className="space-y-3">
          {dados.por_vendedor.map((v, idx) => {
            const isOpen = expandido[v.nome]
            const todosEmbarcados = v.saldo_curral <= 0
            return (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleVendedor(v.nome)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-lg ${todosEmbarcados ? 'bg-green-100' : 'bg-orange-100'}`}>
                      {todosEmbarcados
                        ? <Check size={14} className="text-green-700" />
                        : <Truck size={14} className="text-orange-700" />
                      }
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-semibold text-gray-800">{v.nome}</span>
                      <span className="text-xs text-gray-500 ml-2">Entrada: {v.entrada} cab</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {todosEmbarcados ? (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Completo</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
                        Curral: {v.saldo_curral} cab
                      </span>
                    )}
                    <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    {/* Barra de progresso visual */}
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Entrada: {v.entrada}</span>
                        <span>Saldo: {v.saldo_curral}</span>
                      </div>
                      <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden flex">
                        {v.vendidos > 0 && (
                          <div className="bg-green-500 h-full" style={{ width: `${(v.vendidos / v.entrada) * 100}%` }} title={`Vendidos: ${v.vendidos}`} />
                        )}
                        {v.defesa > 0 && (
                          <div className="bg-red-400 h-full" style={{ width: `${(v.defesa / v.entrada) * 100}%` }} title={`Defesa: ${v.defesa}`} />
                        )}
                        {v.nao_movimentados > 0 && (
                          <div className="bg-amber-400 h-full" style={{ width: `${(v.nao_movimentados / v.entrada) * 100}%` }} title={`Não mov: ${v.nao_movimentados}`} />
                        )}
                      </div>
                      <div className="flex gap-4 mt-1 text-xs">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full" /> Vendidos: {v.vendidos}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-400 rounded-full" /> Defesa: {v.defesa}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-full" /> Não mov: {v.nao_movimentados}</span>
                      </div>
                    </div>

                    {/* Detalhes vendas */}
                    {v.detalhes_vendas?.length > 0 && (
                      <div className="mb-2">
                        <h5 className="text-xs font-semibold text-green-700 mb-1">Vendas por comprador:</h5>
                        <div className="space-y-1">
                          {v.detalhes_vendas.map((dv, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs bg-green-50 rounded px-2 py-1">
                              <ArrowRight size={10} className="text-green-500" />
                              <span className="text-gray-700">{dv.comprador_nome}</span>
                              <span className="font-medium text-green-800 ml-auto">{dv.qtd_lotes} cab</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Detalhes defesa */}
                    {v.detalhes_defesa?.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-red-700 mb-1">Lotes em defesa:</h5>
                        <div className="space-y-1">
                          {v.detalhes_defesa.map((dd, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs bg-red-50 rounded px-2 py-1">
                              <AlertTriangle size={10} className="text-red-500" />
                              <span className="text-gray-700">Lote {dd.lote_numero}</span>
                              <span className="font-medium text-red-800 ml-auto">{dd.qtd} cab</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Vazio */}
      {dados && !loading && dados.por_vendedor?.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Truck size={36} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Nenhuma entrada registrada para este leilão.</p>
        </div>
      )}
    </div>
  )
}
